import axiosInstance from './axiosCreator';
import {
  AnatomySection,
  LiveQuestion,
  LiveQuestionInput,
  Video,
  VideoLibraryItem,
} from '../types/Video';

export interface GroupedVideosBucket {
  topicId: string;
  topicPath: string;
  topicName: string;
  videos: VideoLibraryItem[];
  group?: VideoBucketGroup;
}

export type VideoBucketGroup = 'systems' | 'subjects' | 'specialties' | 'other';

export interface YoutubeChannelFilter {
  key: string;
  label: string;
  videoCount: number;
}

export interface VideoLibraryResponse {
  videos: VideoLibraryItem[];
  groupedVideoBuckets: GroupedVideosBucket[];
  youtubeChannels?: YoutubeChannelFilter[];
  total?: number;
  page?: number;
  pageSize?: number;
  hasMore?: boolean;
}

export interface VideoLibraryQuery {
  page: number;
  pageSize: number;
  view: string;
  sort?: string;
  search?: string;
  topicId?: string;
  source?: string;
}

export interface VideoLibraryFacetTopic {
  topicId: string;
  topicPath: string;
  topicName: string;
  group: VideoBucketGroup;
  videoCount: number;
}

export type VideoLibraryFacetSourceKind =
  'ORA' | 'OSMOSIS' | 'ANATOMY' | 'YOUTUBE_CHANNEL' | 'YOUTUBE_OTHER';

export interface VideoLibraryFacetSource {
  key: string;
  label: string;
  kind: VideoLibraryFacetSourceKind;
  videoCount: number;
}

export interface VideoLibraryFacetSelection {
  topic: Pick<VideoLibraryFacetTopic, 'topicId' | 'topicName' | 'group'> | null;
  source: Pick<VideoLibraryFacetSource, 'key' | 'label' | 'kind'> | null;
}

export interface VideoLibraryFacetsResponse {
  topics: VideoLibraryFacetTopic[];
  sources: VideoLibraryFacetSource[];
  selection: VideoLibraryFacetSelection;
  total: number;
  totalDurationSeconds: number;
  sourceCount: number;
}

export interface VideoLibraryFacetsQuery {
  topicId?: string;
  source?: string;
}

export const getVideoLibrary = async (
  params?: VideoLibraryQuery,
  signal?: AbortSignal
): Promise<VideoLibraryResponse> => {
  try {
    const response = await axiosInstance.get('/videos/library', {
      params,
      signal,
    });

    return response.data as VideoLibraryResponse;
  } catch (error) {
    console.error('Error fetching video library:', error);
    throw error;
  }
};

export const getVideoLibraryFacets = async (
  params?: VideoLibraryFacetsQuery,
  signal?: AbortSignal
): Promise<VideoLibraryFacetsResponse> => {
  try {
    const response = await axiosInstance.get('/videos/library/facets', {
      params,
      signal,
    });

    return response.data as VideoLibraryFacetsResponse;
  } catch (error) {
    console.error('Error fetching video library facets:', error);
    throw error;
  }
};

export const getVideoById = async (videoId: string) => {
  try {
    const response = await axiosInstance.get(`/videos/${videoId}`);
    return response.data as Video;
  } catch (error) {
    console.error('Error fetching video:', error);
    throw error;
  }
};

export const addVideo = async (video: {
  isAnatomyVideo: boolean;
  title: string;
  description?: string;
  mp4Url: string;
  webmUrl: string;
  thumbnailUrl: string;
  vttUrl?: string;
  citationAuthor?: string;
  citationLicense?: string;
  citationUrl?: string;
  anatomySection?: AnatomySection | null;
  topicId?: string;
}) => {
  try {
    const response = await axiosInstance.post('/videos/', video);
    return response.data;
  } catch (error) {
    console.error('Error adding video:', error);
    throw error;
  }
};

export const updateVideo = async (
  videoId: string,
  video: {
    title: string;
    description: string;
    mp4Url: string;
    webmUrl: string;
    order: number;
    citationAuthor: string;
    citationLicense: string;
    citationUrl: string;
    thumbnailUrl: string;
    vttUrl?: string;
    youtubeURL?: string;
    anatomySection: AnatomySection | null;
    videoTabVisibility: boolean;
  }
) => {
  try {
    const response = await axiosInstance.put(`/videos/${videoId}`, video);
    return response.data;
  } catch (error) {
    console.error('Error updating video:', error);
    throw error;
  }
};

// Replace a video's live questions with the submitted list (id-preserving sync)
export const syncLiveQuestions = async (
  videoId: string,
  liveQuestions: LiveQuestionInput[]
): Promise<LiveQuestion[]> => {
  try {
    const response = await axiosInstance.put(
      `/videos/${videoId}/live-questions`,
      { liveQuestions }
    );

    return response.data.data as LiveQuestion[];
  } catch (error) {
    console.error('Error saving live questions:', error);
    throw error;
  }
};

// Record a user's answer to a live question (real-time, one upsert per answer).
export const saveLiveQuestionResponse = async (
  videoId: string,
  liveQuestionId: string,
  selectedOptionId: string,
  studySessionId?: string | null
): Promise<{
  liveQuestionId: string;
  selectedOptionId: string;
  isCorrect: boolean;
  round: number;
}> => {
  const response = await axiosInstance.post(
    `/videos/${videoId}/live-questions/${liveQuestionId}/response`,
    { selectedOptionId, studySessionId }
  );

  return response.data.data;
};

// Reset a video's live questions for the current user (bumps the round; prior
// answers are retained as history). Returns the new current round.
export const resetLiveQuestions = async (videoId: string): Promise<number> => {
  const response = await axiosInstance.post(
    `/videos/${videoId}/live-questions/reset`
  );

  return response.data.data.currentRound as number;
};

// Increment Video View Count
export const incrementVideoView = async (videoId: string) => {
  try {
    const response = await axiosInstance.post(`/videos/${videoId}/view`);
    return response.data;
  } catch (error) {
    console.error('Error incrementing video view:', error);
    throw error;
  }
};
